import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import { CreateTariffModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/tariff/create-tariff.model';
import { PriceComponentModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/tariff/price-component.model';
import { TariffElementModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/tariff/tariff-element.model';
import { TariffDimensionType } from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpi/src/enums/ocpi-protocol-enums';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { TariffCreateFormController } from '@app/feature/tariff/tariff-create/tariff-create-form/tariff-create-form.controller';
import {
  CreateTariffContextProvider,
  useTariffUIContext,
} from '@app/feature/tariff/tariff.context';
import { useCpmsBoCreateTariffMutation } from '@service/cpms-bo-tariff.service';

interface TariffCreateControllerProps {
  onSuccessfulCreate: () => void;
  onCancel: () => void;
}

export const TariffCreateController = ({
  onSuccessfulCreate,
  onCancel,
}: TariffCreateControllerProps): React.JSX.Element => {
  const { t } = useTranslation();
  const [handleCreate] = useCpmsBoCreateTariffMutation();
  const { flatValue } = useTariffUIContext();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  const handleSubmit = (data: CreateTariffModel) => {
    setMultiStepFormState(MultiStepFormState.Loading);

    const cleanElements = data.elements.reduce((accEl, el) => {
      if (!el.priceComponents || el.priceComponents.length === 0) {
        return accEl;
      }

      const priceComponents = el.priceComponents.reduce((acc, pr) => {
        if (!pr.price || !pr.stepSize) {
          return acc;
        }

        return [
          ...acc,
          {
            price: pr.price,
            stepSize: pr.stepSize,
            type: pr.type,
          },
        ];
      }, [] as PriceComponentModel[]);

      if (flatValue) {
        priceComponents.push({
          price: flatValue,
          stepSize: 1,
          type: TariffDimensionType.FLAT,
        });
      }

      return [
        ...accEl,
        {
          ...el,
          priceComponents,
        },
      ];
    }, [] as TariffElementModel[]);

    handleCreate({
      ...data,
      elements: cleanElements,
    })
      .unwrap()
      .then(() => {
        toast.success(t('$.tariff-create.success'));
        onSuccessfulCreate();
      })
      .catch(() => {
        toast.error(t('$.tariff-create.error'));
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  const handleError: SubmitErrorHandler<CreateTariffModel> = (errors) => {
    Logger.error(errors);
    toast.error(t('$.tariff-create.error', { error: t('global.unknown') }));
  };

  return (
    <CreateTariffContextProvider data={{}}>
      <TariffCreateFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </CreateTariffContextProvider>
  );
};
